// Phase 86AZ.57 — ISG (Occupational Health & Safety) Module
// =========================================================
// Contractor self-service: register project personnel, upload ISG documents
// per person, and once Ultra Admin approves a worker, print a barcoded
// "Yaka Kartı" (ID badge).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Barcode from 'react-barcode';
import {
  Plus, Trash2, Edit2, Save, X, FileText, Upload, Download, Printer,
  CheckCircle, XCircle, Clock, ShieldCheck, Paperclip, AlertCircle, IdCard,
} from 'lucide-react';
import { getToken } from '../lib/apiClient';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso, tr) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(tr ? 'tr-TR' : 'en-US', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const StatusPill = ({ status, tr }) => {
  const map = {
    pending:  { cls: 'bg-amber-100 text-amber-700',   icon: Clock,         label: tr ? 'Onay Bekliyor' : 'Pending' },
    approved: { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: tr ? 'Onaylandı'    : 'Approved' },
    rejected: { cls: 'bg-red-100 text-red-700',        icon: XCircle,      label: tr ? 'Reddedildi'   : 'Rejected' },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

function tcValid(tc) {
  if (!/^\d{11}$/.test(tc)) return false;
  if (tc[0] === '0') return false;
  const d = tc.split('').map(Number);
  const odd  = d[0] + d[2] + d[4] + d[6] + d[8];
  const even = d[1] + d[3] + d[5] + d[7];
  if ((odd * 7 - even) % 10 !== d[9]) return false;
  if (d.slice(0, 10).reduce((a, b) => a + b, 0) % 10 !== d[10]) return false;
  return true;
}

// ─── Yeni Kayıt (Add/Edit personnel rows) ────────────────────────────────
function WorkerRegisterTab({ tr, token, workers, refresh }) {
  const [draft, setDraft] = useState(null); // { full_name, tc_no, phone, email, _editId? }
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);
  const auth = { Authorization: `Bearer ${token}` };

  const startAdd = () => { setDraft({ full_name: '', tc_no: '', phone: '', email: '' }); setErrs({}); };
  const startEdit = (w) => { setDraft({ _editId: w.id, full_name: w.full_name, tc_no: w.tc_no, phone: w.phone || '', email: w.email || '' }); setErrs({}); };
  const cancel = () => { setDraft(null); setErrs({}); };

  const save = async () => {
    const e = {};
    if (!draft.full_name?.trim()) e.full_name = tr ? 'Ad Soyad zorunludur.' : 'Full name is required.';
    if (!tcValid(draft.tc_no || '')) e.tc_no = tr ? 'TC kimlik no geçersiz.' : 'Invalid Turkish TC ID.';
    setErrs(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      if (draft._editId) {
        await axios.patch(`${API}/api/isg/workers/${draft._editId}`, {
          full_name: draft.full_name, tc_no: draft.tc_no, phone: draft.phone, email: draft.email,
        }, { headers: auth });
      } else {
        await axios.post(`${API}/api/isg/workers`, draft, { headers: auth });
      }
      cancel();
      await refresh();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = d || (status === 401 ? (tr ? 'Oturum süresi dolmuş, lütfen tekrar giriş yapın.' : 'Session expired, please log in again.')
                     : (tr ? 'Kayıt başarısız.' : 'Save failed.'));
      alert(msg);
      console.error('[ISG worker save] status=%s detail=%o', status, d);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(tr ? 'Bu personel ve tüm belgeleri silinsin mi?' : 'Delete this person and all documents?')) return;
    try {
      await axios.delete(`${API}/api/isg/workers/${id}`, { headers: auth });
      await refresh();
    } catch (err) { alert(err?.response?.data?.detail || 'failed'); }
  };

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2">{tr ? 'Ad Soyad' : 'Full Name'}</th>
              <th className="text-left px-4 py-2">{tr ? 'TC Kimlik No' : 'TC ID'}</th>
              <th className="text-left px-4 py-2">{tr ? 'Telefon' : 'Phone'}</th>
              <th className="text-left px-4 py-2">{tr ? 'E-Posta' : 'Email'}</th>
              <th className="text-left px-4 py-2">{tr ? 'Belge' : 'Docs'}</th>
              <th className="text-left px-4 py-2">{tr ? 'Durum' : 'Status'}</th>
              <th className="text-right px-4 py-2">{tr ? 'İşlem' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {workers.length === 0 && !draft && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                {tr ? 'Henüz personel kaydı yok. Aşağıdan + ile ekleyin.' : 'No personnel yet. Add one below with +.'}
              </td></tr>
            )}
            {workers.map(w => (
              draft?._editId === w.id ? (
                <tr key={w.id} className="bg-amber-50/40">
                  <td className="px-4 py-2"><input className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1" value={draft.full_name} onChange={e => setDraft({...draft, full_name: e.target.value})} data-testid="isg-edit-name" /></td>
                  <td className="px-4 py-2"><input className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1" value={draft.tc_no} onChange={e => setDraft({...draft, tc_no: e.target.value.replace(/\D/g, '').slice(0, 11)})} data-testid="isg-edit-tc" /></td>
                  <td className="px-4 py-2"><input className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1" value={draft.phone} onChange={e => setDraft({...draft, phone: e.target.value})} /></td>
                  <td className="px-4 py-2"><input className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1" value={draft.email} onChange={e => setDraft({...draft, email: e.target.value})} /></td>
                  <td className="px-4 py-2 text-xs text-gray-400">{w.document_count || 0}</td>
                  <td className="px-4 py-2"><StatusPill status={w.status} tr={tr} /></td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={save} disabled={saving} data-testid="isg-edit-save" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50"><Save className="w-3 h-3" /> {tr ? 'Kaydet' : 'Save'}</button>
                    <button onClick={cancel} className="ml-1 inline-flex items-center px-2 py-1 rounded-lg text-gray-500 hover:bg-gray-100 text-xs"><X className="w-3 h-3" /></button>
                  </td>
                </tr>
              ) : (
                <tr key={w.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2 font-semibold text-gray-800">{w.full_name}</td>
                  <td className="px-4 py-2 font-mono text-gray-700">{w.tc_no}</td>
                  <td className="px-4 py-2 text-gray-600">{w.phone || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{w.email || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">{w.document_count || 0}</td>
                  <td className="px-4 py-2"><StatusPill status={w.status} tr={tr} /></td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => startEdit(w)} className="p-1.5 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-50" data-testid={`isg-row-edit-${w.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(w.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`isg-row-delete-${w.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              )
            ))}
            {draft && !draft._editId && (
              <tr className="bg-orange-50/40">
                <td className="px-4 py-2">
                  <input autoFocus className={`w-full text-sm border rounded-lg px-2 py-1 ${errs.full_name ? 'border-red-300' : 'border-gray-200'}`}
                    placeholder={tr ? 'Ad Soyad' : 'Full Name'} value={draft.full_name}
                    onChange={e => setDraft({...draft, full_name: e.target.value})}
                    data-testid="isg-new-name" />
                  {errs.full_name && <p className="text-[10px] text-red-600 mt-0.5">{errs.full_name}</p>}
                </td>
                <td className="px-4 py-2">
                  <input className={`w-full text-sm border rounded-lg px-2 py-1 font-mono ${errs.tc_no ? 'border-red-300' : 'border-gray-200'}`}
                    placeholder="11 hane" value={draft.tc_no}
                    onChange={e => setDraft({...draft, tc_no: e.target.value.replace(/\D/g, '').slice(0, 11)})}
                    data-testid="isg-new-tc" />
                  {errs.tc_no && <p className="text-[10px] text-red-600 mt-0.5">{errs.tc_no}</p>}
                </td>
                <td className="px-4 py-2">
                  <input className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1" placeholder="+90…" value={draft.phone} onChange={e => setDraft({...draft, phone: e.target.value})} data-testid="isg-new-phone" />
                </td>
                <td className="px-4 py-2">
                  <input className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1" placeholder="kisi@firma.com" value={draft.email} onChange={e => setDraft({...draft, email: e.target.value})} data-testid="isg-new-email" />
                </td>
                <td className="px-4 py-2 text-xs text-gray-400">0</td>
                <td className="px-4 py-2"><StatusPill status="pending" tr={tr} /></td>
                <td className="px-4 py-2 text-right">
                  <button onClick={save} disabled={saving} data-testid="isg-new-save" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50"><Save className="w-3 h-3" /> {tr ? 'Kaydet' : 'Save'}</button>
                  <button onClick={cancel} className="ml-1 inline-flex items-center px-2 py-1 rounded-lg text-gray-500 hover:bg-gray-100 text-xs"><X className="w-3 h-3" /></button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!draft && (
        <button type="button" onClick={startAdd} data-testid="isg-add-row"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-dashed border-orange-300 text-orange-600 hover:bg-orange-50 text-sm font-semibold transition-colors">
          <Plus className="w-4 h-4" /> {tr ? 'Yeni Personel Ekle' : 'Add New Person'}
        </button>
      )}
    </div>
  );
}

// ─── Belgeler (Per-worker document upload) ──────────────────────────────
function WorkerDocumentsTab({ tr, token, workers, refresh, openLightbox }) {
  const [selectedId, setSelectedId] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const auth = { Authorization: `Bearer ${token}` };
  const selected = workers.find(w => w.id === selectedId);

  const fetchDocs = async (wid) => {
    if (!wid) { setDocs([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/isg/workers/${wid}/documents`, { headers: auth });
      setDocs(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    fetchDocs(selectedId);
  }, [selectedId]);

  const reload = () => fetchDocs(selectedId);

  const upload = async (files) => {
    if (!selectedId || !files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      [...files].forEach(f => fd.append('files', f));
      await axios.post(`${API}/api/isg/workers/${selectedId}/documents`, fd, {
        headers: { ...auth, 'Content-Type': 'multipart/form-data' },
      });
      await reload();
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || (tr ? 'Yükleme başarısız.' : 'Upload failed.'));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removeDoc = async (id) => {
    if (!window.confirm(tr ? 'Bu belge silinsin mi?' : 'Delete this document?')) return;
    try {
      await axios.delete(`${API}/api/isg/documents/${id}`, { headers: auth });
      await reload();
      await refresh();
    } catch (e) { alert(e?.response?.data?.detail || 'failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-4">
        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">
          {tr ? 'Personel Seçin' : 'Select Person'}
        </label>
        <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
          data-testid="isg-doc-worker-select"
          className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300">
          <option value="">— {tr ? 'Personel seçin' : 'Select a person'} —</option>
          {workers.map(w => (
            <option key={w.id} value={w.id}>
              {w.full_name} · {w.tc_no} · {w.document_count || 0} {tr ? 'belge' : 'docs'} · {w.status}
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-gray-800">{selected.full_name}</p>
              <p className="text-xs text-gray-400 font-mono">{selected.tc_no}</p>
            </div>
            <StatusPill status={selected.status} tr={tr} />
          </div>

          <input ref={fileRef} type="file" multiple className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif"
            onChange={(e) => upload(e.target.files)} data-testid="isg-doc-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="isg-doc-upload-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 border-dashed border-orange-300 text-orange-600 hover:bg-orange-50 text-sm font-semibold disabled:opacity-40">
            <Upload className="w-4 h-4" />
            {uploading ? (tr ? 'Yükleniyor…' : 'Uploading…') : (tr ? 'ISG Belgesi Yükle (çoklu)' : 'Upload ISG Documents (multiple)')}
          </button>

          {loading ? (
            <p className="text-center text-xs text-gray-400 py-6">{tr ? 'Yükleniyor…' : 'Loading…'}</p>
          ) : docs.length === 0 ? (
            <p className="text-center text-xs text-gray-400 py-6">{tr ? 'Bu personel için henüz belge yok.' : 'No documents yet for this person.'}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {docs.map(d => {
                const url = `${API}/api/uploads/${d.filename}`;
                const isImg = /\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(d.original_name || d.filename);
                const isPdf = /\.pdf$/i.test(d.original_name || d.filename);
                return (
                  <div key={d.id} className="relative group bg-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                    <button type="button"
                      onClick={() => openLightbox({ url, name: d.original_name, ext: isImg ? 'img' : isPdf ? 'pdf' : 'other' })}
                      className="block w-full aspect-square bg-white"
                      data-testid={`isg-doc-tile-${d.id}`}>
                      {isImg ? (
                        <img src={url} alt={d.original_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 p-2">
                          <FileText className="w-10 h-10 mb-1" />
                          <span className="text-[9px] font-mono uppercase">{isPdf ? 'PDF' : (d.original_name?.split('.').pop() || 'FILE')}</span>
                        </div>
                      )}
                    </button>
                    <div className="px-2 py-1.5">
                      <p className="text-[10px] text-gray-600 truncate font-medium">{d.original_name}</p>
                      <p className="text-[9px] text-gray-400">{((d.size || 0) / 1024).toFixed(0)} KB</p>
                    </div>
                    <button type="button" onClick={() => removeDoc(d.id)}
                      className="absolute top-1 right-1 p-1 rounded-full bg-white/80 hover:bg-red-500 hover:text-white text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`isg-doc-delete-${d.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {selected.status === 'rejected' && selected.rejected_reason && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span className="font-bold">{tr ? 'Red gerekçesi: ' : 'Reject reason: '}</span>{selected.rejected_reason}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Yaka Kartları (Badges) ──────────────────────────────────────────────
function BadgeTab({ tr, workers }) {
  const approved = workers.filter(w => w.status === 'approved');
  const [printing, setPrinting] = useState(null);

  if (approved.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <IdCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">{tr ? 'Onaylı personel olduğunda yaka kartı buradan oluşturulur.' : 'Once a person is approved, generate the badge here.'}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {approved.map(w => (
        <BadgeCard key={w.id} w={w} tr={tr} onPrint={() => setPrinting(w)} />
      ))}
      {printing && <PrintBadgeModal w={printing} tr={tr} onClose={() => setPrinting(null)} />}
    </div>
  );
}

function BadgeCard({ w, tr, onPrint }) {
  return (
    <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-sm overflow-hidden">
      <BadgeBody w={w} compact />
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
        <span className="text-[10px] text-gray-400">{tr ? 'Onay: ' : 'Approved: '}{w.approved_at ? new Date(w.approved_at).toLocaleDateString() : '—'}</span>
        <button type="button" onClick={onPrint} data-testid={`isg-badge-print-${w.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold">
          <Printer className="w-3 h-3" /> {tr ? 'Yazdır' : 'Print'}
        </button>
      </div>
    </div>
  );
}

function BadgeBody({ w, compact = false }) {
  const sz = compact ? { name: 'text-base', meta: 'text-[10px]', tc: 'text-sm', barW: 1.6, barH: 50 }
                     : { name: 'text-2xl', meta: 'text-xs',      tc: 'text-lg', barW: 2.4, barH: 80 };
  return (
    <div className="px-5 pt-4 pb-3 flex flex-col items-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50">
      <div className="text-[10px] font-bold tracking-widest text-emerald-700">COP 31 ANTALYA</div>
      <div className="text-[9px] uppercase tracking-wider text-gray-400 mb-1">ISG Yaka Kartı / Personnel ID</div>
      <p className={`font-extrabold text-gray-800 text-center ${sz.name}`}>{w.full_name}</p>
      <p className={`font-mono text-gray-600 ${sz.tc}`}>{w.tc_no}</p>
      <p className={`text-gray-500 mt-1 ${sz.meta} text-center`}>
        {w.contractor_name}
        {w.phone ? ` · ${w.phone}` : ''}
        {w.email ? ` · ${w.email}` : ''}
      </p>
      <div className="mt-2 bg-white px-2 py-1 rounded">
        <Barcode value={w.id} format="CODE128" displayValue={!compact} width={sz.barW} height={sz.barH} margin={0} />
      </div>
    </div>
  );
}

function PrintBadgeModal({ w, tr, onClose }) {
  const print = () => {
    const node = document.getElementById('isg-badge-print-area');
    if (!node) return window.print();
    const html = node.outerHTML;
    const win = window.open('', '_blank', 'width=520,height=720');
    if (!win) return;
    win.document.write(`<!doctype html><html><head><title>ISG ${w.full_name}</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;margin:0;padding:24px;background:#fff;}
        .card{width:85mm;border:2px solid #10b981;border-radius:16px;padding:18px;text-align:center;background:linear-gradient(135deg,#ecfdf5,#fff,#ecfdf5);}
        .h{font-size:10px;font-weight:700;letter-spacing:.2em;color:#047857;}
        .sub{font-size:9px;text-transform:uppercase;letter-spacing:.15em;color:#94a3b8;margin-bottom:8px;}
        .name{font-size:20px;font-weight:800;color:#1f2937;margin:4px 0;}
        .tc{font-family:monospace;font-size:16px;color:#475569;}
        .meta{font-size:10px;color:#64748b;margin-top:4px;}
        svg{margin-top:8px;}
      </style></head><body>
      <div class="card">
        <div class="h">COP 31 ANTALYA</div>
        <div class="sub">ISG Yaka Kartı / Personnel ID</div>
        <div class="name">${w.full_name}</div>
        <div class="tc">${w.tc_no}</div>
        <div class="meta">${w.contractor_name}${w.phone ? ' · ' + w.phone : ''}${w.email ? ' · ' + w.email : ''}</div>
        <svg id="bc"></svg>
      </div>
      <script>JsBarcode("#bc","${w.id}",{format:"CODE128",width:2.4,height:80,displayValue:true,margin:0});setTimeout(()=>{window.print();},400);<\/script>
      </body></html>`);
    win.document.close();
  };
  return (
    <div className="fixed inset-0 z-[10010] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="isg-badge-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-800">{tr ? 'Yaka Kartı Önizleme' : 'Badge Preview'}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div id="isg-badge-print-area" className="rounded-xl border-2 border-emerald-200 m-5 overflow-hidden">
          <BadgeBody w={w} />
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">{tr ? 'Kapat' : 'Close'}</button>
          <button onClick={print} data-testid="isg-badge-print-trigger"
            className="px-4 py-2 rounded-lg text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5">
            <Printer className="w-4 h-4" /> {tr ? 'Yazdır' : 'Print'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ISG Section (Contractor side) ─────────────────────────────────
export default function ContractorISG({ tr }) {
  const [tab, setTab] = useState('register');
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { url, name, ext }
  const token = getToken();
  const auth = { Authorization: `Bearer ${token}` };

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/api/isg/workers`, { headers: auth });
      setWorkers(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
  }, []);

  const tabs = [
    { id: 'register',  label: tr ? 'Yeni Kayıt' : 'Register',  icon: Plus },
    { id: 'documents', label: tr ? 'Belgeler'   : 'Documents', icon: FileText },
    { id: 'badges',    label: tr ? 'Yaka Kartları' : 'Badges', icon: IdCard,
      badge: workers.filter(w => w.status === 'approved').length || null },
  ];

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-gray-100 p-2 flex gap-1 overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`isg-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${
                active ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50'
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
              {t.badge ? (
                <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold ${
                  active ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'
                }`}>{t.badge}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-12 text-sm">{tr ? 'Yükleniyor…' : 'Loading…'}</p>
      ) : tab === 'register' ? (
        <WorkerRegisterTab tr={tr} token={token} workers={workers} refresh={refresh} />
      ) : tab === 'documents' ? (
        <WorkerDocumentsTab tr={tr} token={token} workers={workers} refresh={refresh} openLightbox={setLightbox} />
      ) : (
        <BadgeTab tr={tr} workers={workers} />
      )}

      {lightbox && <DocLightbox lightbox={lightbox} tr={tr} onClose={() => setLightbox(null)} />}
    </div>
  );
}

// ─── Shared simple lightbox for ISG documents ───────────────────────────
export function DocLightbox({ lightbox, tr, onClose }) {
  return (
    <div className="fixed inset-0 z-[10020] bg-black/90 flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="isg-doc-lightbox">
      <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between text-white bg-gradient-to-b from-black/60 to-transparent">
        <span className="text-xs text-white/80 truncate max-w-[60%]">{lightbox.name}</span>
        <div className="flex items-center gap-2">
          <a href={`${lightbox.url}${lightbox.url.includes('?') ? '&' : '?'}disposition=attachment`} download target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold">
            <Download className="w-3 h-3" /> {tr ? 'İndir' : 'Download'}
          </a>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="max-w-[92vw] max-h-[88vh] w-full h-full flex items-center justify-center px-10 py-14">
        {lightbox.ext === 'img' ? (
          <img src={lightbox.url} alt={lightbox.name} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        ) : lightbox.ext === 'pdf' ? (
          <iframe src={lightbox.url} title={lightbox.name} className="w-full h-full bg-white rounded-lg shadow-2xl" />
        ) : (
          <div className="bg-white rounded-2xl p-10 text-center max-w-md shadow-2xl">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-bold text-gray-800 break-all">{lightbox.name}</p>
            <p className="text-xs text-gray-400 mt-1">{tr ? 'Tarayıcıda önizlenemiyor.' : 'Cannot preview in browser.'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
